import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Tabs } from 'antd'
import { ExclamationIcon, RefreshIcon } from '@heroicons/react/outline'

import { Button, ButtonLoader, NewTable, Notification, PageTopBar, PageView } from '../../components'
import { Loader } from '../../components/Loader'
import { SlidePanel } from '../../components/SlidePanel'
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime'
import { getStatusDefinition, STOCK_STATUSES } from '../../components/BasicTable/StatusPillDefinitions'
import { UserContext } from '../../hooks/UserContext'
import { FOUND_STOCK } from '../../navigation/constants'
import { setFoundStockPageSize } from '../../redux/pageSizeSlice'
import { fetchFoundStock, fetchFoundStockAwaitingPutAway } from '../../services/foundStockServices'
import { retryUnlessUnauthorized } from './foundStockErrors'
import { foundStockQueryKeys } from './foundStockQueryKeys'
import { useProductSummaries } from './useProductSummaries'
import { LotCell } from './LotCell'
import { ProductCell } from './ProductCell'
import { candidatesTone, maxMatchable, sortAwaitingGroups, summaryFromPayload } from './foundStockRows'
import { parseDeepLink } from './foundStockLinks'
import { MatchFoundStockDialog } from './MatchFoundStockDialog'
import { InboundFoundStockDialog } from './InboundFoundStockDialog'
import { CorrectFoundStockDialog } from './CorrectFoundStockDialog'
import { FoundStockDetailPanel } from './FoundStockDetailPanel'

const T = 'found_stock'
const TONE_CLASSES = {
  full: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  none: 'bg-gray-100 text-gray-600',
}
// The three settlement dialogs take the same props; only which one to mount differs.
const DIALOGS = { match: MatchFoundStockDialog, inbound: InboundFoundStockDialog, correct: CorrectFoundStockDialog }

// Row actions live inside a clickable row: the click must not also open the drawer.
const stop = fn => e => {
  e.stopPropagation()
  fn()
}

// A failed read is not an empty queue. Saying so above the table (and offering the retry) keeps the
// muted empty text meaning exactly one thing: there is nothing to settle.
const LoadErrorBanner = ({ message, retryLabel, onRetry }) => (
  <div className="flex items-start gap-3 rounded-md bg-amber-50 border border-amber-200 p-4 mb-3 text-amber-800" role="alert">
    <ExclamationIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
    <p className="flex-1 text-sm">{message}</p>
    <Button type="secondary" className="whitespace-nowrap" onClick={onRetry}>
      {retryLabel}
    </Button>
  </div>
)

const FoundStockPage = ({ warehouseId: warehouse_id }) => {
  const { i18n } = useTranslation()
  const { user } = useContext(UserContext)
  const dispatch = useDispatch()
  const pageSize = useSelector(state => state.pageSize.foundStockList)
  const { TabPane } = Tabs

  // Deep link from the cycle-counts page (spec §2.2): parsed once on mount; later in-page navigation
  // (the drawer's SlidePanel pushes the route on close) must not reset the filters.
  //
  //   ?location_code=…  ─► queueParams.location_code  ─┬─► the queue read + a visible filter chip
  //   ?declaration_id=… ─► queueParams.declaration_id ─┤
  //                                                    └─► detailId: the drawer opens straight away
  //                                                        (a settled declaration has left the queue)
  //   ?cycle_count_id=… ─► highlightCycleCountId ─┬─► a notice + a border cue on its rows
  //                                               └─► total === groups.length AND exactly one match
  //                                                   ─► the drawer auto-opens once (didAutoOpen)
  const { search } = useLocation()
  const [initial] = useState(() => parseDeepLink(search, pageSize))

  const [activeTab, setActiveTab] = useState('queue')
  const [queueParams, setQueueParams] = useState(initial.params)
  const [highlightCycleCountId, setHighlightCycleCountId] = useState(initial.highlightCycleCountId)
  const didAutoOpen = useRef(false)
  const [staleMessage, setStaleMessage] = useState(null)
  const [openById, setOpenById] = useState('')
  const queryClient = useQueryClient()
  const [gateOff, setGateOff] = useState(false)
  const [dialog, setDialog] = useState(null) // { kind: 'match'|'inbound'|'correct', declarationId }
  const [detailId, setDetailId] = useState(initial.openDeclarationId)
  // `seq` is what makes each message its own <Notification>: the component arms its 6 s dismissal in
  // an effect keyed on `show`, so a second toast fired while the first is up would neither
  // re-animate nor get its own timer.
  const [notification, setNotification] = useState({ show: false, type: 'success', title: '', seq: 0 })
  const notify = useCallback((type, title) => setNotification(n => ({ show: true, type, title, seq: n.seq + 1 })), [])

  // Write authority is the platform's settle_found_stock key; an undefined key (older platform) hides everything.
  const canSettle = user?.permissions?.settle_found_stock === true
  const showActions = canSettle && !gateOff

  const queue = useQuery(foundStockQueryKeys.queue(warehouse_id, queueParams), () => fetchFoundStock(warehouse_id, queueParams), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    // A reconnect must not yank an open dialog out from under a write in progress.
    refetchOnReconnect: false,
    retry: retryUnlessUnauthorized,
  })
  const awaitingQuery = useQuery(foundStockQueryKeys.awaiting(warehouse_id), () => fetchFoundStockAwaitingPutAway(warehouse_id), {
    refetchOnWindowFocus: false,
    enabled: activeTab === 'awaiting',
    retry: retryUnlessUnauthorized,
  })

  const groups = useMemo(() => queue.data?.groups || [], [queue.data])
  const awaitingGroups = useMemo(() => sortAwaitingGroups(awaitingQuery.data?.groups), [awaitingQuery.data])

  // Settling the only group on a later page must not strand the user on an empty page.
  useEffect(() => {
    const d = queue.data
    if (!d || d.total === 0 || (d.groups || []).length > 0 || queueParams.page <= 1) return
    // A response without per_page would make the clamp NaN and cost one page=NaN request.
    if (!(d.per_page > 0)) return
    setQueueParams(p => ({ ...p, page: Math.max(1, Math.ceil(d.total / d.per_page)) }))
  }, [queue.data, queueParams.page])

  // A dialog acts on the live group, never on a snapshot. If a refetch drops the group (settled
  // elsewhere), close the dialog and always say why — a silent disappearance mid-write reads as a
  // click that did nothing.
  const selectedGroup = groups.find(g => g.declaration_id === dialog?.declarationId)
  useEffect(() => {
    if (!dialog || queue.isFetching || selectedGroup) return
    setDialog(null)
    notify('error', staleMessage || i18n.t(`${T}.errors.CONCURRENT_MODIFICATION`))
    setStaleMessage(null)
  }, [dialog, selectedGroup, queue.isFetching, staleMessage, notify, i18n])

  // A deep link naming one cycle count opens its declaration straight away (once) — but only when the
  // whole result is on this page; "one match on page 1 of 3" is not "one match".
  useEffect(() => {
    if (highlightCycleCountId == null || didAutoOpen.current || !queue.data) return
    if (queue.data.total !== groups.length) return
    const matches = groups.filter(g => g.cycle_count_id === highlightCycleCountId)
    if (matches.length === 1) {
      didAutoOpen.current = true
      setDetailId(matches[0].declaration_id)
    }
  }, [groups, highlightCycleCountId, queue.data])

  // Only the visible tab's rows need a product lookup, and only those the platform did not enrich:
  // the awaiting read is unpaginated, so resolving it while the queue tab is open is pure fan-out.
  const activeRows = activeTab === 'awaiting' ? awaitingGroups : groups
  const productIds = useMemo(() => activeRows.filter(row => !summaryFromPayload(row)).map(row => row.product_id), [activeRows])
  const summaries = useProductSummaries(productIds)
  // Spec §7.1: the settlement dialogs name the product, resolved the same way as the queue cell.
  const selectedProduct = selectedGroup ? summaryFromPayload(selectedGroup) || summaries[selectedGroup.product_id] : null

  // Plain functions: the react-query result objects change every render, so memoising these buys nothing.
  const refetchAll = () => {
    queue.refetch()
    if (activeTab === 'awaiting') awaitingQuery.refetch()
  }

  // Cancel and settle both leave no pending stale message behind: it would otherwise become the
  // text of a later notification when a refetch happens to drop the group.
  const closeDialog = () => {
    setDialog(null)
    setStaleMessage(null)
  }

  const onSettled = message => {
    closeDialog()
    notify('success', message)
    refetchAll()
    // The hub badge caches the unsettled total for a minute; a settlement changes it now.
    queryClient.invalidateQueries(foundStockQueryKeys.queueTotal(warehouse_id))
    // Spec §7: an open detail drawer shows the settled declaration's audit; refresh it too.
    queryClient.invalidateQueries(foundStockQueryKeys.declaration(warehouse_id))
  }
  const onStale = message => {
    setStaleMessage(message)
    queue.refetch()
  }
  const onGateOff = () => {
    setDialog(null)
    setStaleMessage(null)
    setGateOff(true)
  }

  const queueColumns = useMemo(
    () =>
      [
        { Header: i18n.t(`${T}.queue.declaration`), accessor: 'declaration' },
        { Header: i18n.t(`${T}.queue.product`), accessor: 'product' },
        { Header: i18n.t(`${T}.queue.lot`), accessor: 'lot' },
        { Header: i18n.t(`${T}.queue.location`), accessor: 'location' },
        { Header: i18n.t(`${T}.queue.quantity`), accessor: 'quantity' },
        { Header: i18n.t(`${T}.queue.declared_by`), accessor: 'declared_by' },
        { Header: i18n.t(`${T}.queue.source`), accessor: 'source' },
        { Header: i18n.t(`${T}.queue.candidates`), accessor: 'candidates' },
        ...(showActions ? [{ Header: i18n.t(`${T}.queue.actions`), accessor: 'actions' }] : []),
        // Nothing here is sortable server-side; without this NewTable draws sort chevrons that do
        // nothing (precedent: ListCycleCountsContainer's locations columns).
      ].map(column => ({ ...column, disableSortBy: true })),
    [i18n, showActions],
  )

  const queueRows = useMemo(
    () =>
      groups.map(group => {
        const tone = candidatesTone(group)
        const nothingToMatch = maxMatchable(group) === 0
        return {
          object: { ...group, id: group.declaration_id },
          declaration: (
            <div>
              <div>#{group.declaration_id}</div>
              {group.cycle_count_id != null && <div className="text-xs text-gray-500">{i18n.t(`${T}.queue.cycle_count`, { id: group.cycle_count_id })}</div>}
            </div>
          ),
          product: <ProductCell productId={group.product_id} summary={summaryFromPayload(group) || summaries[group.product_id]} />,
          lot: <LotCell row={group} />,
          location: group.location_code,
          quantity: (
            <div>
              <div className="font-semibold">{group.quantity}</div>
              {group.declared_quantity !== group.quantity && (
                <div className="text-xs text-gray-500">{i18n.t(`${T}.queue.declared_quantity`, { count: group.declared_quantity })}</div>
              )}
            </div>
          ),
          declared_by: (
            <div>
              <div>{group.declared_by_name || `#${group.declared_by_id}`}</div>
              <FormattedRelativeTime date={group.declared_at} className="text-xs text-gray-500" />
            </div>
          ),
          source: (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {i18n.t(`${T}.source.${group.source}`, { defaultValue: group.source })}
            </span>
          ),
          candidates: (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TONE_CLASSES[tone]}`}>{group.match_candidates}</span>
          ),
          actions: showActions ? (
            <div className="flex gap-2 whitespace-nowrap">
              {/* A disabled button gets no mouse events, so the hint has to hang off a wrapper. */}
              <span title={nothingToMatch ? i18n.t(`${T}.queue.match_disabled_hint`) : undefined}>
                <ButtonLoader type="primary" disabled={nothingToMatch} onClick={stop(() => setDialog({ kind: 'match', declarationId: group.declaration_id }))}>
                  {i18n.t(`${T}.queue.match_button`)}
                </ButtonLoader>
              </span>
              <ButtonLoader type="secondary" onClick={stop(() => setDialog({ kind: 'inbound', declarationId: group.declaration_id }))}>
                {i18n.t(`${T}.queue.inbound_button`)}
              </ButtonLoader>
              <ButtonLoader type="secondary" onClick={stop(() => setDialog({ kind: 'correct', declarationId: group.declaration_id }))}>
                {i18n.t(`${T}.queue.correct_button`)}
              </ButtonLoader>
            </div>
          ) : null,
        }
      }),
    [groups, summaries, showActions, i18n],
  )

  const chip = (key, placeholder, label) => ({
    key,
    placeholder: `${T}.queue.filters.${placeholder}`,
    label: `${T}.queue.${label}`,
    data: { visible: initial.params[key] != null, type: 'text', value: initial.params[key] ?? '' },
  })
  const [queueFilters, setQueueFilters] = useState(() => [
    chip('location_code', 'location', 'location'),
    chip('declaration_id', 'declaration', 'declaration'),
    chip('product_id', 'product', 'product'),
  ])
  const onQueueSearch = (updated, key) => {
    setQueueFilters(prev => prev.map(f => (f.key === key ? { ...f, data: updated } : f)))
    setQueueParams(prev => {
      const next = { ...prev, page: 1 }
      if (updated.visible && updated.value !== '') next[key] = updated.value
      else delete next[key]
      return next
    })
  }
  const resetQueueFilters = () => setQueueParams(prev => ({ page: 1, per_page: prev.per_page }))

  const queueError = queue.isError
  const queueForbidden = queue.error?.response?.status === 401
  const queueErrorMessage = queueForbidden ? i18n.t(`${T}.queue.forbidden`) : i18n.t(`${T}.queue.load_error`)
  const paginationMeta = queue.data
    ? {
        current_page: queue.data.page,
        per_page: queue.data.per_page,
        total_count: queue.data.total,
        // Same guard as the page clamp above: a response without per_page would render NaN pages.
        total_pages: queue.data.per_page > 0 ? Math.max(1, Math.ceil(queue.data.total / queue.data.per_page)) : 1,
      }
    : {}

  const awaitingColumns = useMemo(
    () =>
      [
        { Header: i18n.t(`${T}.awaiting.location`), accessor: 'location' },
        { Header: i18n.t(`${T}.awaiting.product`), accessor: 'product' },
        { Header: i18n.t(`${T}.awaiting.lot`), accessor: 'lot' },
        { Header: i18n.t(`${T}.awaiting.status`), accessor: 'status' },
        { Header: i18n.t(`${T}.awaiting.quantity`), accessor: 'quantity' },
      ].map(column => ({ ...column, disableSortBy: true })),
    [i18n],
  )
  const awaitingRows = useMemo(
    () =>
      awaitingGroups.map((g, index) => ({
        object: { id: `${g.warehouse_location_id}-${g.product_id}-${g.lot_id}-${g.status}-${index}` },
        location: g.location_code,
        product: <ProductCell productId={g.product_id} summary={summaryFromPayload(g) || summaries[g.product_id]} />,
        lot: <LotCell row={g} />,
        // The status pill table is Spanish-only; give the three statuses this bay can hold their own
        // localized copy and fall back to that table for anything the platform adds later.
        status: i18n.t(`${T}.awaiting.status_${g.status}`, { defaultValue: getStatusDefinition(g.status, STOCK_STATUSES).label }),
        quantity: g.quantity,
      })),
    [awaitingGroups, summaries, i18n],
  )

  const DialogComponent = dialog ? DIALOGS[dialog.kind] : null

  return (
    <>
      <Notification
        key={notification.seq}
        show={notification.show}
        setShow={show => setNotification(n => ({ ...n, show }))}
        title={notification.title}
        type={notification.type}
      />

      {DialogComponent && selectedGroup && (
        <DialogComponent
          open
          warehouseId={warehouse_id}
          group={selectedGroup}
          product={selectedProduct}
          onSuccess={onSettled}
          onCancel={closeDialog}
          onGateOff={onGateOff}
          onStale={onStale}
        />
      )}

      {detailId != null && (
        <SlidePanel
          title={i18n.t(`${T}.detail.title`)}
          // SlidePanel pushes the referrer on close: keeping the current search means closing the
          // drawer leaves the deep link in the URL instead of silently widening the view.
          referrer={`${FOUND_STOCK.replace(':warehouse_id', warehouse_id)}${search}`}
          onClose={() => setDetailId(null)}
        >
          <FoundStockDetailPanel warehouseId={warehouse_id} declarationId={detailId} />
        </SlidePanel>
      )}

      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              {i18n.t(`${T}.title`)}{' '}
              <span className="text-sm text-gray-400">
                {i18n.t(`${T}.subtitle`)} - {i18n.t('common.warehouse')} {warehouse_id}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* A settled declaration leaves the queue; its audit stays reachable by id. */}
              <form
                className="flex items-center gap-1"
                onSubmit={e => {
                  e.preventDefault()
                  const id = Number(openById)
                  if (Number.isInteger(id) && id > 0) setDetailId(id)
                }}
              >
                <label htmlFor="found-stock-open-by-id" className="text-sm text-gray-600 whitespace-nowrap">
                  {i18n.t(`${T}.open_by_id_label`)}
                </label>
                {/* An id is a digit string, not a measurement: text + inputMode keeps the numeric
                    keypad on a handheld without the spinner and the scroll-wheel edits, and
                    `required` makes an empty submit say something instead of nothing. */}
                <input
                  id="found-stock-open-by-id"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                  value={openById}
                  onChange={e => setOpenById(e.target.value)}
                  className="w-24 rounded-md border-gray-300 text-sm"
                />
                {/* ButtonLoader has no submit escape hatch (its `type` is the style variant); Button's buttonType is one. */}
                <Button type="secondary" className="whitespace-nowrap" buttonType="submit">
                  {i18n.t(`${T}.open_by_id_button`)}
                </Button>
              </form>
              <ButtonLoader type="secondary" className="whitespace-nowrap" onClick={() => refetchAll()} loading={queue.isFetching || awaitingQuery.isFetching}>
                <RefreshIcon className="mr-1 -ml-1 h-4 w-4" aria-hidden="true" />
                {i18n.t(`${T}.refresh`)}
              </ButtonLoader>
            </div>
          </PageTopBar>
        }
      >
        {gateOff && (
          <div className="flex items-start gap-3 rounded-md bg-amber-50 border border-amber-200 p-4 mb-4 text-amber-800" role="status">
            <ExclamationIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-medium">{i18n.t(`${T}.gate.banner_title`)}</p>
              <p className="text-sm">{i18n.t(`${T}.gate.banner_body`)}</p>
            </div>
          </div>
        )}
        <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
          <TabPane tab={i18n.t(`${T}.tabs.queue`)} key="queue">
            {highlightCycleCountId != null && (
              <div className="flex items-center gap-3 rounded-md bg-indigo-50 border border-indigo-200 p-3 mb-3 text-sm text-indigo-900" role="status">
                <span className="flex-1">{i18n.t(`${T}.queue.highlight_notice`, { id: highlightCycleCountId })}</span>
                <button
                  type="button"
                  className="font-medium underline hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                  onClick={() => setHighlightCycleCountId(null)}
                >
                  {i18n.t(`${T}.queue.highlight_clear`)}
                </button>
              </div>
            )}
            {queueError && <LoadErrorBanner message={queueErrorMessage} retryLabel={i18n.t(`${T}.queue.retry`)} onRetry={() => queue.refetch()} />}
            <div className="relative">
              <Loader show={queue.isFetching && queue.isPreviousData} />
              {!queue.isPreviousData && queue.isLoading ? (
                <Loader show />
              ) : (
                <NewTable
                  columns={queueColumns}
                  // Keep the table (and its filter bar) mounted even on a load error, so the reset
                  // control needed to clear a filter the backend rejected stays reachable.
                  data={queueError ? [] : queueRows}
                  filtersData={queueFilters}
                  onSearchHandler={onQueueSearch}
                  handleResetFilters={resetQueueFilters}
                  paginationMeta={paginationMeta}
                  onPaginationChange={page => setQueueParams(prev => ({ ...prev, page }))}
                  onPageSizeChange={size => {
                    // NewTable hands over the SelectInput's { id, name } option, not a number.
                    const perPage = Number(size?.id ?? size)
                    setQueueParams(prev => ({ ...prev, per_page: perPage, page: 1 }))
                    dispatch(setFoundStockPageSize(perPage))
                  }}
                  showHeader
                  // The banner above already says the read failed; leaving the empty copy out keeps
                  // it meaning exactly one thing — there is nothing left to settle.
                  emptyTableText={queueError ? null : i18n.t(`${T}.queue.empty_table`)}
                  cursorPointer
                  rowProps={row => ({
                    onClick: () => setDetailId(row.original.object.id),
                    // react-table's mergeProps drops NewTable's own row className once this one is non-empty,
                    // so a highlighted row has to restate the base classes it would otherwise lose.
                    className:
                      highlightCycleCountId != null && row.original.object.cycle_count_id === highlightCycleCountId
                        ? 'bg-indigo-50 border-l-4 border-indigo-400 border cursor-pointer hover:bg-indigo-100'
                        : undefined,
                  })}
                />
              )}
            </div>
          </TabPane>
          <TabPane tab={i18n.t(`${T}.tabs.awaiting`)} key="awaiting">
            <p className="text-sm text-gray-500 mb-3">{i18n.t(`${T}.awaiting.hint`)}</p>
            {awaitingQuery.isError && (
              <LoadErrorBanner message={i18n.t(`${T}.awaiting.load_error`)} retryLabel={i18n.t(`${T}.queue.retry`)} onRetry={() => awaitingQuery.refetch()} />
            )}
            <div className="relative">
              {awaitingQuery.isLoading ? (
                <Loader show />
              ) : (
                <NewTable
                  columns={awaitingColumns}
                  data={awaitingQuery.isError ? [] : awaitingRows}
                  showHeader
                  showFiltersHeader={false}
                  emptyTableText={awaitingQuery.isError ? null : i18n.t(`${T}.awaiting.empty_table`)}
                  cursorPointer={false}
                />
              )}
            </div>
          </TabPane>
        </Tabs>
      </PageView>
    </>
  )
}

// The route stays mounted across a sidebar warehouse switch; keying the page on the warehouse resets
// queries, filters, the gate-off banner and any open dialog instead of showing the old warehouse's rows.
export const FoundStockContainer = () => {
  const { warehouse_id } = useParams()
  return <FoundStockPage key={warehouse_id} warehouseId={warehouse_id} />
}
